import { and, assign, or } from 'xstate';
import { GUARDS } from './guards';
import { ACTIONS } from './actions';
import { REGISTER_STEPS_TYPES, Step } from './types';
import { trackEvent } from '@monofront/tracking';

export const buildStates = ({ steps }: { steps: Step[] }) => {
  const states = {};
  const seenUIDs = new Set<string>();

  const registerStepIndex = steps.findIndex(
    ({ type }) => type === REGISTER_STEPS_TYPES.REGISTER,
  );

  if (registerStepIndex === -1) {
    throw new Error('buildStates: no REGISTER step found in steps array');
  }

  steps.forEach((currentStep, currentStepIndex) => {
    const { uid: currentUID } = currentStep;

    if (seenUIDs.has(currentUID)) {
      throw new Error(
        `buildStates: duplicate step uid detected: ${currentUID}`,
      );
    }
    seenUIDs.add(currentUID);

    const isFirst = currentStepIndex === 0;
    const isLast = currentStepIndex === steps.length - 1;
    const prevUID = isFirst ? null : steps[currentStepIndex - 1].uid;
    const nextUID = isLast ? null : steps[currentStepIndex + 1].uid;

    states[currentUID] = {
      always: [
        {
          // user should not be logged out and access steps after register step
          guard: and([
            GUARDS.IS_NOT_AUTHENTICATED,
            GUARDS.IS_STEP_AFTER_REGISTER,
          ]),
          target: steps[registerStepIndex].uid,
        },
        {
          // user should not be signed in and access the register step or any step before it
          guard: and([
            GUARDS.IS_AUTHENTICATED,
            or([GUARDS.IS_STEP_BEFORE_REGISTER, GUARDS.IS_STEP_REGISTER]),
          ]),
          target: nextUID,
        },
      ],
      entry: [
        assign(() => ({
          currentStepIndex,
          currentStep: {
            type: currentStep.type,
            uid: currentStep.uid,
            trackingData: currentStep.trackingData,
          },
          registerStepIndex,
        })),
        ({ context }) => {
          trackEvent({
            type: 'LFPageView',
            data: context.currentStep.trackingData ?? {},
          });
        },
      ],
      on: {
        [ACTIONS.NEXT]: nextUID
          ? {
              target: nextUID,
              actions: [ACTIONS.NEXT],
            }
          : { actions: [ACTIONS.FINISH_FORM] },
        [ACTIONS.PREVIOUS]: prevUID
          ? {
              target: prevUID,
              actions: [ACTIONS.PREVIOUS],
            }
          : { actions: [ACTIONS.EXIT_FORM] },
      },
    };
  });

  return states;
};
